import { SolanaWithSignerBaseCommand } from '../../../../solana';

import { Idl, Program, web3 } from '@coral-xyz/anchor';
import { Flags } from '@oclif/core';
import { NATIVE_MINT } from '@solana/spl-token';
import { State } from '@switchboard-xyz/on-demand';

export const TOKEN_PROGRAM_ID = new web3.PublicKey(
  'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'
);

export default class UnwrapDelegationPoolRewards extends SolanaWithSignerBaseCommand {
  static enableJsonFlag = true;

  static description =
    'Close the delegation pools WSOL reward account and unwrap SOL to the oracle authority';

  static flags = {
    ...SolanaWithSignerBaseCommand.flags,
    delegationPool: Flags.string({
      description: 'The delegation pool address to unwrap rewards from',
      required: true,
    }),
    solDestination: Flags.string({
      description:
        'Optional. Destination account to receive unwrapped SOL. Defaults to the payer.',
      required: false,
    }),
  };

  async run() {
    const { flags } = await this.parse(
      this.constructor as typeof UnwrapDelegationPoolRewards
    );

    const provider = this.provider;
    const authority = this.payer;
    const isDevnet = this.cluster === 'devnet';

    const STAKING_PID = new web3.PublicKey(
      'SBSTk6t52R89MmCdD739Rdd97HdbTQUFHe41vCX7pTt'
    );
    const ON_DEMAND_PID = new web3.PublicKey(
      isDevnet
        ? 'Aio4gaXjXzJNVLtzwtNVmSqGKpANtXhybbkhtAC94ji2'
        : 'SBondMDrcV3K4kxZR1HNVT7osZxAHVHgYXL5Ze1oMUv'
    );

    this.logger.info(`✅ Cluster: ${this.cluster}`);
    this.logger.info(`✅ Staking program ID: ${STAKING_PID}`);
    this.logger.info(`✅ OnDemand program ID: ${ON_DEMAND_PID}`);
    this.logger.info(`✅ Delegation pool: ${flags.delegationPool}`);
    this.logger.info(`✅ Oracle Authority: ${authority.toString()}`);

    // derive accounts
    const delegationPoolKey = new web3.PublicKey(flags.delegationPool);
    const solDestination = new web3.PublicKey(
      flags.solDestination ?? authority
    );

    const stakingIdl = await Program.fetchIdl(STAKING_PID, provider);
    const stakingProgram = new Program(stakingIdl as Idl, provider);

    const ondemandIdl = await Program.fetchIdl(ON_DEMAND_PID, provider);
    const ondemandProgram = new Program(ondemandIdl as Idl, provider);

    const stateKey = State.keyFromSeed(ondemandProgram);

    const delegationPoolAccount = await (
      stakingProgram.account as {
        delegationPool: {
          fetch: (pubkey: web3.PublicKey) => Promise<{
            oracleData: web3.PublicKey;
            stakePool: web3.PublicKey;
          }>;
        };
      }
    ).delegationPool.fetch(delegationPoolKey);

    const oracleStats = await (
      ondemandProgram.account as {
        oracleStatsAccountData: {
          fetch: (
            pubkey: web3.PublicKey
          ) => Promise<{ oracle: web3.PublicKey }>;
        };
      }
    ).oracleStatsAccountData.fetch(delegationPoolAccount.oracleData);

    const oracleAccountKey = oracleStats.oracle;

    const rewardVault = web3.PublicKey.findProgramAddressSync(
      [
        Buffer.from('RewardPool'),
        delegationPoolKey.toBuffer(),
        NATIVE_MINT.toBuffer(),
      ],
      STAKING_PID
    )[0];

    // log all accounts before sending to debug
    this.logger.info(`✅ Oracle account: ${oracleAccountKey.toString()}`);
    this.logger.info(`✅ Reward vault: ${rewardVault.toString()}`);
    this.logger.info(
      `✅ Sol destination (default to payer): ${solDestination.toString()}`
    );
    this.logger.info(`✅ Oracle stats: ${delegationPoolAccount.oracleData}`);
    this.logger.info(`✅ Stake pool: ${delegationPoolAccount.stakePool}`);
    // reward vault and sol_destinatino balance before and after
    const rewardVaultBalance = await this.connection.getBalance(
      rewardVault,
      'confirmed'
    );
    this.logger.info(
      `✅ Reward vault balance BEFORE: ${rewardVaultBalance} lamports`
    );
    const solDestinationBalance = await this.connection.getBalance(
      solDestination,
      'confirmed'
    );
    this.logger.info(
      `✅ Sol destination balance BEFORE: ${solDestinationBalance} lamports`
    );

    const delegationPoolSeeds = [
      Buffer.from('Delegation'),
      stateKey.toBuffer(),
      delegationPoolAccount.oracleData.toBuffer(),
      delegationPoolAccount.stakePool.toBuffer(),
    ];
    const [delegationPoolPDA] = web3.PublicKey.findProgramAddressSync(
      delegationPoolSeeds,
      STAKING_PID
    );
    if (!delegationPoolPDA.equals(delegationPoolKey)) {
      throw new Error(
        '❌ Derived PDA does not match provided delegationPool address.\n'
      );
    }
    const unwrapIx = await stakingProgram.methods
      .unwrapDelegationPoolVault({})
      .accounts({
        oracleAuthority: authority,
        stateAccount: stateKey,
        stakePool: delegationPoolAccount.stakePool,
        delegationPool: delegationPoolKey,
        oracleStats: delegationPoolAccount.oracleData,
        oracleAccountData: oracleAccountKey,
        rewardVault,
        solDestination,
        tokenProgram: TOKEN_PROGRAM_ID,
      })
      .instruction();

    const tx = new web3.Transaction().add(unwrapIx);
    tx.feePayer = this.payer;
    const latestBlockhash = (
      await this.connection.getLatestBlockhash('confirmed')
    ).blockhash;
    tx.recentBlockhash = latestBlockhash;

    // Simulate
    const simResult = await this.connection.simulateTransaction(tx);
    if (simResult.value.err === null) {
      this.logger.info('✅ Simulation successful, sending transaction...');
      await this.signAndSend([unwrapIx]);

      // check balance after
      const newRewardVaultBalance = await this.connection.getBalance(
        rewardVault,
        'confirmed'
      );
      this.logger.info(
        `Reward vault balance AFTER: ${newRewardVaultBalance} lamports`
      );
      const newSolDestinationBalance = await this.connection.getBalance(
        solDestination,
        'confirmed'
      );
      this.logger.info(
        `Sol destination balance AFTER: ${newSolDestinationBalance} lamports`
      );
      this.logger.info(
        `✅ Unwrapped ${rewardVaultBalance - newRewardVaultBalance} lamports to ${solDestination.toString()}`
      );
    } else {
      this.logger.error('❌ Simulation failed, transaction not sent.');
      throw new Error(`Simulation failed: ${JSON.stringify(simResult)}`);
    }
  }

  async catch(error: unknown) {
    super.catch(error, 'failed to unwrap delegation pool rewards');
  }
}
